import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import * as FlipdishApi from '@flipdish/api-client-typescript';
import BankIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import FinanceIcon from '@mui/icons-material/AttachMoneyOutlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CodeIcon from '@mui/icons-material/Code';
import DeveloperIcon from '@mui/icons-material/CodeOutlined';
import CreditCard from '@mui/icons-material/CreditCard';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import WebsiteIcon from '@mui/icons-material/LanguageOutlined';
import AuditLogsIcon from '@mui/icons-material/ListOutlined';
import VouchersIcon from '@mui/icons-material/LoyaltyOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import TeammatesIcon from '@mui/icons-material/PeopleOutlined';
import PermPhoneMsgOutlined from '@mui/icons-material/PermPhoneMsgOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DevicesIcon from '@mui/icons-material/PhonelinkSetup';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import RestaurantOutlined from '@mui/icons-material/RestaurantOutlined';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import StoresIcon from '@mui/icons-material/StoreOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import Verified from '@mui/icons-material/Verified';

import appStoreIcon from '../../assets/images/Appstore.svg';
import campaignsIcon from '../../assets/images/campaigns.svg';
import { ReactComponent as CatalogIcon } from '../../assets/images/catalog.svg';
import generalSettingsIcon from '../../assets/images/General_settings.svg';
import customersIcon from '../../assets/images/ico_customers.svg';
import ledgerIcon from '../../assets/images/Ledger.svg';
import payoutsIcon from '../../assets/images/Payouts.svg';
import { ReactComponent as ProductIcon } from '../../assets/images/Products.svg';
import salesChannelsIcon from '../../assets/images/sales-channels-icon.svg';
import EntitlementsIcon from '../../assets/images/StickyNote.svg';
import { KioskIcon } from '../../components/Kiosks/Components/KioskNestedMenuIcon';
import { shouldShowRMSMenuEditor } from '../../components/MenuEditor/utils';
import { useHasAccessToOnboardingV3 } from '../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import { propertiesPermissions } from '../../components/Properties/propertiesPermissions';
import { accountsPermissions } from '../../components/RMS/Accounts/permissions';
import { inventoryPermissions } from '../../components/RMS/Inventory/inventoryPermissions';
import { payrollPermissions } from '../../components/RMS/Payroll/payrollPermissions';
import { MenuSource } from '../../components/RMS/rms.services';
import {
  franchisorPermissions,
  permissionsIncludingPropertyOwner,
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../components/RMS/utils/permissions';
import { RMSSubscriptions } from '../../components/RMS/utils/rmsSubscriptions';
import GeneralSettingsIcon from '../../components/Settings/components/GeneralSettingsIcon';
import { entitlementConstants } from '../../constants/entitlement.constants';
import useHasEntitlement from '../../custom-hooks/useHasEntitlement';
import { store } from '../../helpers/store';
import { PhoneIcon } from '../../icons';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../services/featureFlags/useBooleanFeatureFlagResolution';
import { useFeatureFlagReady } from '../../services/featureFlags/useFeatureFlagReady';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { type FrontEndFlag } from '../../services/flagService/featureFlagList';
import { flagService } from '../../services/flagService/flagService';
import { removeOmittedMenuItems } from './removeOmittedMenuItems';
import { getSalesOrdersNavChoice } from './salesOrdersNavGate';

export type ItemMetaData = {
  hidden?: boolean;
  /**
   * When true, the item is removed from the nav entirely (hard-omit).
   * Unlike `hidden`, Show hidden features does not override this.
   */
  omit?: boolean;
  icon: JSX.Element;
  iconAnimated?: JSX.Element;
  isBeta?: boolean;
  isMFE?: boolean;
  path: string;
  permissions?: AppState['permissions'];
  step?: number;
  title: TranslationId;
  searchAlias?: string[];
  subItems?: Array<{
    title: TranslationId;
    path: string;
    icon?: React.ReactElement;
    hidden?: boolean;
    /**
     * When true, the item is removed from the nav entirely (hard-omit).
     * Unlike `hidden`, Show hidden features does not override this.
     */
    omit?: boolean;
    isBeta?: boolean;
    keepQuery?: boolean;
    permissions?: AppState['permissions'];
    isMFE?: boolean;
    searchAlias?: string[];
  }>;
  subDrawerItems?: Array<{
    title: TranslationId;
    path: string;
    icon?: React.ReactElement;
    hidden?: boolean;
    /**
     * When true, the item is removed from the nav entirely (hard-omit).
     * Unlike `hidden`, Show hidden features does not override this.
     */
    omit?: boolean;
    isBeta?: boolean;
    keepQuery?: boolean;
    permissions?: AppState['permissions'];
    isMFE?: boolean;
    searchAlias?: string[];
  }>;
};

export const getMenuListingPath = (
  currentAppId: string,
  isOrgListingV3On: boolean,
  isOrgListingV3Ready: boolean
): string | null => {
  const state = store.getState();
  const isLegacyFlagOn = flagService.isFlagOn(state, 'showMenuListingV3');
  const isListingV3Enabled = isLegacyFlagOn || isOrgListingV3On;
  const isLegacyFlagReady = flagService.isFlagReady(state, 'showMenuListingV3');
  // Pending only while neither flag has resolved yet.
  if (!isListingV3Enabled && !isLegacyFlagReady && !isOrgListingV3Ready) {
    return null;
  }
  if (isListingV3Enabled) {
    return `/${currentAppId}/restaurant-menus`;
  }
  return `/${currentAppId}/menu-editor/menus`;
};

export const isBulkActionEnabled = (): boolean => {
  const state = store.getState();
  const showBulkAction = flagService.isFlagOn(state, 'menuV3_showBulkActions');
  return showBulkAction;
};

export const isSchedulingEnabled = (): boolean => {
  const state = store.getState();
  const showScheduling = flagService.isFlagOn(state, 'menuV3_showScheduling');
  return showScheduling;
};

export const isMenuUnifiedItemsViewEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_showUnifiedItemsView');
};

export const isMenuUnifiedModifiersViewEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_showUnifiedModifiersView');
};

export const isMenuValidationFeedbackEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_showMenuValidationFeedback');
};

export const isMenuSectionBehaviourEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_showMenuSectionBehaviour');
};

export const isDisableDrsForNonIeOrgsEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_disableDrsForNonIeOrgs');
};

export const isPosMetadataEnabled = (): boolean => {
  const state = store.getState();
  return flagService.isFlagOn(state, 'menuV3_showPosMetadata');
};

const getMenuNavItem = (
  currentAppId: string,
  showMenuEditor: boolean,
  reducedOnboardingView: boolean,
  isOrgListingV3On: boolean,
  isOrgListingV3Ready: boolean
): ItemMetaData => {
  const getPath = (basePath: string) => basePath;

  if (showMenuEditor) {
    return {
      title: 'Menu_Editor',
      path: getMenuListingPath(currentAppId, isOrgListingV3On, isOrgListingV3Ready) ?? '',
      icon: <RestaurantOutlined />,
      hidden: reducedOnboardingView,
      permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff', 'PropertyOwner'],
      isMFE: true,
      searchAlias: [
        'Menu',
        'Edit menu',
        'Update menu',
        'Option set',
        'Menu category',
        'Menu pricing',
        'Menu price',
        'Assign menu',
        'Menu image',
      ],
    };
  }

  return {
    title: 'Menus',
    path: getPath(`/${currentAppId}/menus`),
    icon: <RestaurantOutlined />,
    hidden: reducedOnboardingView,
    permissions: ['ViewMenu'],
    searchAlias: [
      'Menu',
      'Edit menu',
      'Update menu',
      'Option set',
      'Menu category',
      'Menu pricing',
      'Menu price',
      'Assign menu',
    ],
  };
};

const shouldShowVerifactu = ({
  showVerifactuFlag,
  orgCountryCode,
  orgId,
  isProduction,
}: {
  showVerifactuFlag: boolean;
  orgCountryCode?: string;
  orgId?: string;
  isProduction: boolean;
}): boolean => {
  // Enabled for production Mexican Cafe for Flipdish testing only.
  if (isProduction && orgId === 'org10030') return true;
  // TODO: Remove flag once its 100% released and we are happy with it.
  return showVerifactuFlag && orgCountryCode === 'ES';
};

export const menuItemsConfig = (
  currentApp: FlipdishApi.App,
  isProductBasedMenusOn: boolean,
  menuSource?: MenuSource,
  subscriptions = Array<RMSSubscriptions>()
): ItemMetaData[] => {
  const { AppId: currentAppId, Features, CountryId } = currentApp;
  const { data } = useQueryRMSCommonDataHook(currentAppId ?? '');
  useAppSelector((s) => flagService.getSplitValue(s, 'showMenuListingV3'));
  const isOrgListingV3On = useFeatureFlagValue(PortalFeatureFlags.showMenuListingV3Org, false);
  const isOrgListingV3Ready = useFeatureFlagReady(PortalFeatureFlags.showMenuListingV3Org);
  useAppSelector((s) => flagService.getSplitValue(s, 'enableClientReportingPayoutsMfe'));
  const state = store.getState();
  const showNewSidebarNavigation = flagService.isFlagOn(state, 'showNewSidebarNavigation');
  const { onboardingStatus, isFlipdishStaff } = useHasAccessToOnboardingV3();
  const isApiFlagOn = data?.associations?.isMigratedToSalesChannels;
  const currentOrg = state.rms.currentOrg;
  const orgCountryCode = currentOrg?.countryCode;
  const orgId = currentOrg?.orgId;
  const { hasEntitlement: hasPhoneAgentEntitlement, isLoading: isLoadingEntitlement } =
    useHasEntitlement(orgId, entitlementConstants.PhoneAgent.AiAgent);
  const permissions = state.permissions ?? [];
  const bankAccountAccessPermissions: AppState['permissions'] = [
    'ViewBankAccounts',
    'FlipdishStaff',
  ];
  const hasBankAccountsAccess = permissions.some((permission) =>
    bankAccountAccessPermissions.includes(permission)
  );
  const isProduction = process.env.ENVIRONMENT === 'production';

  const reducedOnboardingView = onboardingStatus === 'in_progress' && !isFlipdishStaff;
  /**
   * If apiFlag is off, override by adding appId to showSalesChannelStructure FF in Split.
   *
   * e.g api says hide it, then add the appId to the FF in Split to show it
   * */
  const showSalesChannelStructure =
    isApiFlagOn || flagService.isFlagOn(state, 'showSalesChannelStructure');

  /**
   * hideStoresPage2 FF defaults to true. To show stores page, add the appId to the FF in Split.
   *
   * e.g the api says hide the stores page but we want to show it, then add the appId to the FF in Split.
   * When true, Stores is omitted from the nav entirely (hard-omit) — Show hidden features does not override.
   */
  const omitStoresFromNav = isApiFlagOn && flagService.isFlagOn(state, 'hideStoresPage2');

  const showPayGreen = flagService.isFlagOn(store.getState(), 'paygreenFeatures');
  const showSmsCampaigns = flagService.isFlagOn(store.getState(), 'showSmsCampaigns');
  const showVerifactuFlag = flagService.isFlagOn(store.getState(), 'showVerifactuLink');
  const showGoogleListingManagement = flagService.isFlagOn(
    store.getState(),
    'showGoogleListingManagement'
  );
  const disablePropertyManagerLevelAddOns = flagService.isFlagOn(
    store.getState(),
    'disablePropertyManagerLevelAddOns'
  );

  const enableNewWebhooksSystem = flagService.isFlagOn(store.getState(), 'enableNewWebhooksSystem');
  const showAuthMenuItem = flagService.isFlagOn(
    store.getState(),
    'showAuthMenuItem' as FrontEndFlag
  );
  const enableClientReportingPayoutsMfe = flagService.isFlagOn(
    state,
    'enableClientReportingPayoutsMfe'
  );

  const disableLegacyTeammate = flagService.isFlagOn(store.getState(), 'disableLegacyTeammate');

  const foodSafetyAddOnPermissions = flagService.isFlagOn(
    store.getState(),
    'foodSafetyAddOnPermissions'
  );

  /**
   * When false, Entitlements V2 is omitted from the nav entirely (hard-omit) —
   * Show hidden features does not override.
   */
  const omitEntitlementsV2FromNav = !useFeatureFlagValue(
    PortalFeatureFlags.accessEntitlementsV2Page,
    false
  );

  /**
   * When false, AI Marketing is omitted from the nav entirely (hard-omit) —
   * Show hidden features does not override. Mirrors the Harness flag that gates
   * the managed-marketing-service MFE, so the link is only shown where the MFE
   * is actually served (off in production until the MFE is available there).
   */
  const omitAiMarketingFromNav = !useFeatureFlagValue(PortalFeatureFlags.aiMarketing, false);

  /**
   * Org-keyed backend flag (Split traffic type org_id). Wait until the provider
   * is ready before showing Sales or Orders. On / FLAG_NOT_FOUND → Sales,
   * off → Orders; not ready → omit both. flipdish-global always Sales.
   * forceSalesMenuOption ON short-circuits to Sales.
   */
  const salesOrdersNavChoice = getSalesOrdersNavChoice(
    currentAppId,
    useBooleanFeatureFlagResolution(
      PortalFeatureFlags.backend_menu_shouldAssignMenuToStoreFromOomEvents,
      false
    ),
    useFeatureFlagValue(PortalFeatureFlags.forceSalesMenuOption, false)
  );

  const addOnPermissions: AppState['permissions'] = disablePropertyManagerLevelAddOns
    ? franchisorPermissions
    : foodSafetyAddOnPermissions
      ? [...permissionsIncludingPropertyOwner, ...propertyManagerRolePermission]
      : permissionsIncludingPropertyOwner;

  const showMenuEditor = shouldShowRMSMenuEditor(menuSource);

  const menuNavItem = getMenuNavItem(
    currentAppId ?? '',
    showMenuEditor,
    reducedOnboardingView,
    isOrgListingV3On,
    isOrgListingV3Ready
  );

  const paygreenCountries = ['FR', 'BE'];
  const smsCampaignsCountries = ['IE', 'GB'];
  const links: ItemMetaData[] = [
    {
      title: 'Home',
      path: `/${currentAppId}/home`,
      icon: <HomeIcon />,
      searchAlias: ['Main dashboard', 'Overview', 'Quick View'],
      hidden: reducedOnboardingView,
    },
    {
      title: 'Sales',
      path: `/${currentAppId}/sales`,
      icon: <ReceiptIcon />,
      // Same visibility as former Orders: ViewOrders is enough (no BASE gate).
      permissions: ['ViewOrders', 'PropertyOwner', 'Owner', 'ManagedOwner', 'FlipdishStaff'],
      hidden: reducedOnboardingView,
      omit: salesOrdersNavChoice !== 'sales',
      isMFE: true,
      searchAlias: [
        'Online Sales',
        'POS sales',
        'Point of Sale Sales',
        'Sales reports',
        'Sales by product',
        'Orders',
        'Order Management',
        'Customer Orders',
        'Live orders view',
      ],
    },
    {
      title: 'Orders',
      path: `/${currentAppId}/orders`,
      icon: <ReceiptIcon />,
      permissions: ['ViewOrders', 'PropertyOwner', 'Owner', 'ManagedOwner', 'FlipdishStaff'],
      hidden: reducedOnboardingView,
      omit: salesOrdersNavChoice !== 'orders',
      searchAlias: [
        'Order Management',
        'Customer Orders',
        'Live orders view',
        'Sales',
        'Online Sales',
      ],
    },
    {
      title: 'Properties',
      path: `/${currentAppId}/properties`,
      icon: <StoresIcon />,
      permissions: propertiesPermissions,
      hidden: !showSalesChannelStructure || reducedOnboardingView,
      searchAlias: [
        'Opening hours',
        'Collection hours',
        'Delivery hours',
        'General settings',
        'Operating configuration',
        'Restaurant layout',
        'Geo configuration',
        'Address overrides',
        'Float',
        'Localisation configuration',
        'Archive property',
        'Restaurants',
        'Address',
        'Property Address',
        'Takeaway',
        'Restaurants',
        'Franchises',
        'Locations',
      ],
    },
    menuNavItem,
    {
      title: 'Reports',
      path: `/${currentAppId}/reports/dashboards`,
      icon: <BarChartIcon />,
      searchAlias: [
        'Orders Report',
        'Business Reports',
        'Overview Report',
        'End of day report',
        'Performance Summary',
        'Business Review',
        'Ordered Items Report',
        'Tips Report',
        'Campaign Performance Report',
        'Customers Report',
        'Sales Report',
        'Inventory Report',
        'Costs Report',
        'Payroll Report',
      ],
      hidden: reducedOnboardingView,
    },
    {
      title: 'Catalog',
      path: '',
      icon: <CatalogIcon />,
      isBeta: true,
      hidden: !isProductBasedMenusOn || reducedOnboardingView,
      subItems: [
        {
          title: 'Products',
          path: `/${currentAppId}/products`,
          hidden: !isProductBasedMenusOn,
          icon: <ProductIcon color="rgb(108, 107, 107)" />,
          isBeta: true,
        },
        {
          title: 'Modifier_Groups',
          path: `/${currentAppId}/modifier-groups`,
          hidden: !isProductBasedMenusOn,
          icon: <ProductIcon color="rgb(108, 107, 107)" />,
          isBeta: true,
        },
      ],
    },
    {
      title: 'Marketing',
      path: '',
      icon: <CampaignOutlinedIcon />,
      hidden: reducedOnboardingView,
      subItems: [
        {
          title: 'Customers',
          path: `/${currentAppId}/customers`,
          icon: <img src={customersIcon} alt="Customers" />,
          isBeta: false,
          permissions: ['ViewCustomerStatistics'],
          searchAlias: [
            'List of customers',
            'List of consumers',
            'Average order value',
            'Customer name',
            'Customer phone number',
            'Total order value customer',
          ],
        },
        {
          title: 'Vouchers',
          path: `/${currentAppId}/vouchers`,
          icon: <VouchersIcon />,
          permissions: ['ViewVouchers'],
          searchAlias: [
            'Retention voucher',
            'Customer discount',
            'Loyalty voucher',
            'Discount vouchers',
            'Deal codes',
            'Promo codes',
          ],
        },
        {
          title: 'Campaigns',
          path: `/${currentAppId}/campaigns`,
          icon: <img src={campaignsIcon} alt="Campaigns" />,
          permissions: ['ViewCampaignsConfigurations'],
          isBeta: false,
          hidden: Features?.includes('disable-campaign'),
          searchAlias: [
            'Loyalty Campaign',
            'Retention Campaign',
            'Loyalty rewards',
            'Retention rewards',
          ],
        },
        {
          title: 'Push_Notifications',
          path: `/${currentAppId}/push_notifications`,
          icon: <NotificationsIcon />,
          searchAlias: [
            'Schedule push notification',
            'Customer messages',
            'Promote voucher code',
            'Mobile messages',
          ],
        },
        {
          title: 'Flipdish_Phone_Marketing',
          path: `/${currentAppId}/telephony/dashboards/global/flipdish_phone_marketing_fpm_report`,
          icon: <PermPhoneMsgOutlined />,
          searchAlias: [
            'Flipdish Phone Marketing',
            'Flipdish Phone Marketing Report',
            'Flipdish Phone Marketing Dashboard',
            'Flipdish Phone Marketing Overview',
            'Flipdish Phone Marketing Performance',
          ],
        },
        {
          title: 'Flipdish_Phone_Marketing_beta',
          path: `/${currentAppId}/flipdish-phone-marketing/home`,
          icon: <PermPhoneMsgOutlined />,
          permissions: ['FlipdishStaff'],
          isBeta: true,
          isMFE: true,
          searchAlias: ['Setup Phone Marketing'],
        },
        {
          title: 'AI_Marketing',
          path: `/${currentAppId}/ai-marketing`,
          icon: <CampaignOutlinedIcon />,
          permissions: ['FlipdishStaff', 'Owner', 'ManagedOwner'],
          isBeta: true,
          isMFE: true,
          omit: omitAiMarketingFromNav,
          searchAlias: ['AI Marketing', 'Amplify subscription', 'Amplify plans'],
        },
        {
          title: 'SMS_campaigns',
          path: `/${currentAppId}/marketing/campaigns`,
          icon: <img src={campaignsIcon} alt="SMS Campaigns" />,
          hidden:
            CountryId === 'GB'
              ? false
              : !showSmsCampaigns || !smsCampaignsCountries.includes(CountryId || ''),
          isBeta: true,
          isMFE: true,
          searchAlias: [
            'SMS campaign sales revenue',
            'SMS orders from campaigns',
            'Edit SMS campaign',
            'Create SMS campaign',
            'Edit SMS campaign',
          ],
        },
        {
          title: 'Google_Listing_Management',
          path: `/${currentAppId}/marketing/listing-management/google`,
          icon: <StoresIcon />,
          hidden: !showGoogleListingManagement,
          isBeta: true,
          isMFE: true,
          searchAlias: [
            'Manage google business profile',
            'Link google business profile',
            'Unlink google business profile',
            'Google business profile',
          ],
        },
      ],
    },
    {
      title: 'Finance',
      path: '',
      icon: <FinanceIcon />,
      permissions: ['ViewPayouts', 'ViewBankAccounts', 'FlipdishStaff'],
      hidden: reducedOnboardingView,
      subItems: [
        {
          title: 'Payouts',
          path: `/${currentAppId}/finance/payouts`,
          icon: <img src={payoutsIcon} alt="Payouts" />,
          permissions: ['ViewPayouts', 'FlipdishStaff'],
          isMFE: enableClientReportingPayoutsMfe,
          searchAlias: [
            'Payment',
            'Payout report',
            'Disbursement',
            'Remittance',
            'Fund transfer',
            'Earnings report',
            'Bank transfer',
            'Money transfer',
            'Weekly payout',
            'Daily payout',
            'Payment reconciliation report',
            'Balance transfer',
            'Payout summary',
            'Income summary',
            'Merchant account payout',
          ],
        },
        {
          title: 'Bank_accounts',
          path: `/${currentAppId}/finance/bank-accounts`,
          icon: <BankIcon />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          searchAlias: [
            'IBAN',
            'Payout bank account',
            'Payment processing bank account',
            'Edit VAT number',
            'Vat details',
            'Update company information',
            'Registered company number',
            'Company verification',
            'Update my identity documents',
            'Update company number',
            'Edit bank account',
            'Edit bank',
            'Banking details',
            'Edit company account details',
            'Remove bank account',
          ],
        },
        {
          title: 'Restaurant_vouchers',
          path: `/${currentAppId}/finance/restaurant-vouchers`,
          icon: <CreditCard />,
          permissions: ['Owner', ...propertyOwnerRolePermission, 'ManagedOwner', 'FinanceManger'],
          hidden:
            !hasBankAccountsAccess || !showPayGreen || !paygreenCountries.includes(CountryId || ''),
        },
        {
          title: 'Ledger',
          path: `/${currentAppId}/finance/ledger`,
          icon: <img src={ledgerIcon} alt="Ledger" />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          hidden: !hasBankAccountsAccess || !Features?.includes('ledger-menu-item'),
        },
        {
          title: 'Verifactu',
          path: `/${currentAppId}/receipt/verifactu`,
          icon: <Verified />,
          permissions: ['ViewBankAccounts', 'FlipdishStaff'],
          hidden:
            !hasBankAccountsAccess ||
            !shouldShowVerifactu({
              showVerifactuFlag,
              orgCountryCode,
              orgId,
              isProduction,
            }),
          isBeta: true,
          isMFE: true,
          searchAlias: ['Verifactu'],
        },
      ],
    },
    {
      title: 'Sales_channels',
      path: '',
      icon: <img src={salesChannelsIcon} alt="Sales channels" />,
      hidden: reducedOnboardingView,
      permissions: [
        'ViewSalesChannels',
        'EditSalesChannels',
        'ViewHydraConfig',
        ...propertyManagerRolePermission,
        ...propertyOwnerRolePermission,
      ],
      subItems: [
        {
          title: 'Point_of_Sale',
          path: `/${currentAppId}/sales-channels/point-of-sale`,
          icon: <TvOutlinedIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          hidden: !subscriptions.includes(RMSSubscriptions.BASE),
          searchAlias: [
            'POS Settings',
            'Point of Sale configuration',
            'Cash register setup',
            'POS configuration',
            'POS hardware setup',
            'POS hardware settings',
            'POS system settings',
          ],
        },
        {
          title: 'Mobile_apps',
          path: `/${currentAppId}/sales-channels/mobile-apps`,
          icon: <PhoneIphoneIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          searchAlias: [
            'Edit mobile app settings',
            'Edit mobile app sales channels',
            'Edit collection and delivery settings',
            'Mobile app device settings',
            'Change restaurants that appear in the app',
            'Change list of addresses in mobile app',
            'Mobile app configurations',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Website',
          path: `/${currentAppId}/sales-channels/website`,
          icon: <WebsiteIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          searchAlias: [
            'Edit website settings',
            'Edit website sales channels',
            'Edit collection and delivery settings',
            'Online order settings',
            'Order settings',
            'Website device settings',
            'Change restaurants that appear in the website',
            'Change list of addresses in website',
            'Website editor',
            'Website configuration',
            'Domain setup',
            'Cookie management',
            'Website settings',
            'DNS settings',
            'Website builder',
            'Customize website',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Kiosks',
          path: `/${currentAppId}/sales-channels/kiosk`,
          icon: <KioskIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          searchAlias: [
            'Kiosk settings',
            'Kiosk list',
            'Kiosk sales channels',
            'Kiosk configuration',
            'Kiosk home screen customisation',
            'Edit kiosk home screen',
            'Pair kiosk',
            'Add kiosk',
            'Remove kiosk',
            'View kiosks',
            'Edit kiosk sales channels',
            'Configure kiosk',
            'Add table settings',
            'Opening hours',
            'Opening hour overrides',
          ],
        },
        {
          title: 'Phone_agent',
          path: `/${currentAppId}/sales-channels/phone-agent`,
          icon: <PhoneIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          searchAlias: [
            'Phone agent',
            'Phone agent sales channels',
            'AI phone agent',
            'Edit phone agent sales channels',
            'Phone agent configuration',
            'Opening hours',
            'Opening hour overrides',
          ],
          hidden: isLoadingEntitlement || !hasPhoneAgentEntitlement,
        },
        {
          title: 'Marketplaces',
          path: `/${currentAppId}/sales-channels/marketplaces`,
          icon: <StorefrontOutlinedIcon />,
          permissions: [
            'Owner',
            'ManagedOwner',
            'FlipdishStaff',
            ...propertyManagerRolePermission,
            ...propertyOwnerRolePermission,
          ],
          hidden: !showSalesChannelStructure,
          searchAlias: [
            'Marketplace sales channel',
            'Deliveroo',
            'Just Eat',
            'Uber Eats',
            'Add marketplace',
            'Edit marketplace opening hours',
            'Edit marketplace settings',
            'Marketplace configuration',
            'Marketplace devices',
            'Delivery charge marketplace',
            'Marketplace menu',
            'Timing configuration',
          ],
        },
      ],
    },
    {
      title: 'Stores',
      path: `/${currentAppId}/storegroups`,
      icon: <StoresIcon />,
      hidden: reducedOnboardingView,
      omit: omitStoresFromNav,
    },
    {
      title: 'First_Time_Setup',
      path: `/${currentAppId}/setup`,
      icon: <FlightTakeoffIcon />,
      omit: onboardingStatus === 'off',
      permissions: ['Owner', 'FlipdishStaff'],
      isMFE: true,
    },
    {
      title: 'Settings',
      path: '',
      icon: <GeneralSettingsIcon />,
      permissions: ['Owner', 'ManagedOwner', ...propertyOwnerRolePermission],
      hidden: reducedOnboardingView,
      subDrawerItems: [
        {
          title: 'Organization',
          path: `/${currentAppId}/settings/organisation`,
          icon: <RoomPreferencesOutlinedIcon />,
          permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: ['Organisation settings'],
        },
        {
          title: 'Brand',
          path: `/${currentAppId}/settings/brands`,
          icon: <img src={generalSettingsIcon} alt="Brand" />,
          permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: ['Brand settings'],
        },
        {
          title: 'Teammates',
          path: `/${currentAppId}/teammates`,
          icon: <TeammatesIcon />,
          permissions: ['EditTeammates', 'ViewTeammates'],
          searchAlias: ['Add users', 'Remove users', 'Add teammates', 'Remove teammates'],
          hidden: disableLegacyTeammate,
        },
        {
          title: 'Teammate_Permissions',
          path: `/${currentAppId}/settings/teammates`,
          icon: <img src={generalSettingsIcon} alt="Teammate permissions" />,
          permissions: ['ViewDevelopersSettings', 'EditDevelopersSettings'],
          isMFE: true,
          hidden: !showAuthMenuItem,
          searchAlias: ['Teammate Permissions', 'Permissions', 'Authorization', 'Auth', 'Access'],
        },
        {
          title: 'Metafields',
          path: `/${currentAppId}/settings/metafields`,
          icon: <CodeIcon />,
          hidden: !isProductBasedMenusOn,
        },
        {
          title: 'Sales_channels',
          path: `/${currentAppId}/settings/sales-channels`,
          icon: <img src={generalSettingsIcon} alt="Sales channels" />,
          permissions: ['ViewSalesChannels', 'EditSalesChannels'],
          searchAlias: ['Sales channel settings'],
        },
        {
          title: 'Sales_channel_groups',
          path: `/${currentAppId}/settings/sales-channel-groups`,
          icon: <img src={generalSettingsIcon} alt="Sales channel groups" />,
          permissions: ['ViewSalesChannels', 'EditSalesChannels'],
          hidden: !showSalesChannelStructure,
          searchAlias: ['Sales channel groups'],
        },
        {
          title: 'Customer_messages',
          path: `/${currentAppId}/settings/customer-messages/`,
          icon: <img src={generalSettingsIcon} alt="Customer messages" />,
          permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          hidden: !showSalesChannelStructure,
        },
        {
          title: 'Webhooks',
          path: `/${currentAppId}/settings/webhooks`,
          icon: <img src={generalSettingsIcon} alt="Webhooks" />,
          permissions: ['ViewDevelopersSettings', 'EditDevelopersSettings'], // this is not quite the permissions we want to use but we'll revisit this once we have better permissions to use
          hidden: !enableNewWebhooksSystem,
          isMFE: true,
          searchAlias: ['Webhook configuration', 'Webhook settings', 'Webhook management'],
        },
        {
          title: 'Entitlements',
          path: `/${currentAppId}/entitlements`,
          icon: <img src={EntitlementsIcon} alt="Entitlements" />,
          permissions: ['FlipdishStaff', 'ShowHiddenFeatures'],
        },
        {
          title: 'Entitlements_V2',
          path: `/${currentAppId}/entitlements-v2`,
          icon: <img src={EntitlementsIcon} alt="Entitlements V2" />,
          permissions: ['FlipdishStaff'],
          isBeta: true,
          isMFE: true,
          omit: omitEntitlementsV2FromNav,
        },
        {
          title: 'Bulk_Updater',
          path: `/${currentAppId}/bulk-updater`,
          icon: <BuildOutlinedIcon />,
          permissions: ['FlipdishStaff'],
        },
        {
          title: 'Audit_logs',
          path: `/${currentAppId}/logs`,
          icon: <AuditLogsIcon />,
          permissions: [
            'ViewAppAuditLogs',
            'ViewBankAccountAuditLogs',
            'ViewCampaignsAuditLogs',
            'ViewCustomerAuditLogs',
            'ViewFeeConfigurationsAuditLogs',
            'ViewHydraAuditLogs',
            'ViewMenuAuditLogs',
            'ViewOrdersAuditLogs',
            'ViewPrinterAuditLogs',
            'ViewPushNotificationAuditLogs',
            'ViewStoreAuditLogs',
            'ViewTeammatesAuditLogs',
            'ViewUserEventsAuditLogs',
            'ViewVouchersAuditLogs',
            'ViewApmAuditLogs',
          ],
          searchAlias: ['history'],
        },
      ],
    },
    {
      title: 'Add_ons',
      path: '',
      icon: <KeyboardArrowDown />,
      iconAnimated: <KeyboardArrowUp />,
      hidden: reducedOnboardingView,
      subItems: [
        {
          title: 'App_Store',
          path: `/${currentAppId}/appstore`,
          icon: <img src={appStoreIcon} alt="App Store" />,
          permissions: ['Owner'],
          searchAlias: [
            'Google Ordering',
            'Star Micronics cloud print',
            'Uber Direct configuration',
            'Customer Feedback App',
            'Flipdish POS',
            'Glovo Delivery',
          ],
        },
        {
          title: 'Accounts',
          path: `/${currentAppId}/add-ons/accounts`,
          icon: <AccountBalanceWalletOutlinedIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.ACCOUNTS),
          permissions: accountsPermissions,
          searchAlias: [
            'Bills',
            'Credit Accounts',
            'Credit Account Status',
            'Credit Books',
            'Expenses',
            'Transaction Accounts',
            'Account categories',
            'Book keeping',
          ],
        },
        {
          title: 'Documents',
          path: `/${currentAppId}/add-ons/documents`,
          icon: <PlagiarismOutlinedIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.DOCUMENTS),
          permissions: ['EditDocumentExplorer', 'ViewDocumentExplorer'],
        },
        {
          title: 'FoodSafety',
          path: `/${currentAppId}/add-ons/food-safety`,
          icon: <PlagiarismOutlinedIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.FOOD_SAFETY),
          permissions: addOnPermissions,
          searchAlias: [
            'Appliances',
            'Temperature logs',
            'Incident logs',
            'Incident types',
            'Stock item cooking temperature logs',
            'Stock item storage temperature logs',
            'Food safety tasks',
            'Food safety types',
            'Task Logs',
          ],
        },
        {
          title: 'Inventory',
          path: `/${currentAppId}/add-ons/inventory`,
          icon: <AssignmentIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.INVENTORY),
          permissions: inventoryPermissions,
          searchAlias: [
            'Stock',
            'Stock items',
            'Stock item categories',
            'Stock item suppliers',
            'Tracking profiles',
            'Purchase orders',
            'Manage purchase orders',
            'Edit stock items',
            'Suppliers',
          ],
        },
        {
          title: 'Payroll',
          path: `/${currentAppId}/add-ons/payroll`,
          icon: <PaymentsIcon />,
          permissions: payrollPermissions,
          searchAlias: [
            'Holiday planner',
            'Payroll calculator',
            'Edit payroll settings',
            'Edit payroll configuration',
            'Set payroll schedule',
            'Rota',
            'Manage rota',
          ],
        },
        {
          title: 'Signage',
          path: `/${currentAppId}/add-ons/signage`,
          icon: <OndemandVideoOutlinedIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.SIGNAGE),
          permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: [
            'Edit signage',
            'Add signage',
            'Manage signage',
            'Customer display',
            'Active order signage',
            'Signage slide',
            'New signage player',
            'Customer display screen signage',
          ],
        },
        {
          title: 'Staff',
          path: `/${currentAppId}/add-ons/staff`,
          icon: <GroupsOutlinedIcon />,
          permissions: ['ViewPayrollStaff', 'EditPayrollStaff'],
          searchAlias: [
            'Add staff',
            'Manage staff',
            'Define staff roles',
            'Staff permissions',
            'Set staff pin',
            'Set POS pin',
            'Clock in settings',
            'Add kitchen staff',
            'Add POS staff',
            'Add POS users',
            'Add driver to staff',
            'Add staff roles',
            'View staff roles',
          ],
        },
        {
          title: 'Central_kitchen',
          path: `/${currentAppId}/add-ons/central-kitchen`,
          icon: <HubOutlinedIcon />,
          hidden: !subscriptions.includes(RMSSubscriptions.CENTRAL_KITCHEN),
          permissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'],
          searchAlias: [
            'Settings central kitchen',
            'Configuration central kitchen',
            'Manage orders central kitchen',
            'Central kitchen service',
          ],
        },
        {
          title: 'Terminals',
          path: `/${currentAppId}/terminals`,
          icon: <DevicesIcon />,
          permissions: ['ViewHydraConfig'],
          searchAlias: ['Add terminal', 'Delete terminal', 'View terminal'],
        },
      ],
    },
    {
      title: 'Flipdish_Staff_Tools',
      // Old sidebar still links straight to the MFE's own list page (MenuItem.tsx renders
      // a flyout drawer whenever subDrawerItems is present, regardless of path, so that array
      // must only be attached for the new sidebar). Only the new sidebar reuses the
      // sub-navigation pattern to house the tools + hidden features toggle.
      path: showNewSidebarNavigation ? '' : `/${currentAppId}/bulk-updater/home`,
      icon: <AdminPanelSettingsOutlinedIcon />,
      permissions: ['FlipdishStaff'],
      hidden: reducedOnboardingView,
      isMFE: !showNewSidebarNavigation,
      ...(showNewSidebarNavigation && {
        subDrawerItems: [
          {
            title: 'Bulk_Settings_Updater',
            path: `/${currentAppId}/bulk-updater/bulk-fee-updater`,
            icon: <BuildOutlinedIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Advanced_Restaurant_Search',
            path: `/${currentAppId}/bulk-updater/restaurant-search`,
            icon: <StorefrontOutlinedIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Advanced_User_Search',
            path: `/${currentAppId}/bulk-updater/user-search`,
            icon: <TeammatesIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Org_View',
            path: `/${currentAppId}/bulk-updater/org-view`,
            icon: <HubOutlinedIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'System_settings_for_all_orgs',
            path: `/${currentAppId}/bulk-updater/system`,
            icon: <RoomPreferencesOutlinedIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Logo_Resizer',
            path: `/${currentAppId}/bulk-updater/logo-resizer`,
            icon: <img src={generalSettingsIcon} alt="Logo resizer" />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Onboarding_Configuration_Tool',
            path: `/${currentAppId}/bulk-updater/onboarding-configuration`,
            icon: <FlightTakeoffIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Mobile_Apps_Tools',
            path: `/${currentAppId}/mobile-apps-tools`,
            icon: <PhoneIphoneIcon />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
          {
            title: 'Gloria_Food',
            path: `/${currentAppId}/bulk-updater/gloria-food`,
            icon: <RestaurantOutlined />,
            permissions: ['FlipdishStaff'],
            isMFE: true,
          },
        ],
      }),
    },
  ];

  return removeOmittedMenuItems(links);
};

export const menuItemsConfigMobile = (currentAppId: string | undefined): ItemMetaData[] => {
  const items: ItemMetaData[] = [
    {
      title: 'My_account',
      path: `/${currentAppId}/profile`,
      icon: <PersonIcon />,
    },
    {
      title: 'developer_tools',
      path: `/${currentAppId}/developers`,
      icon: <DeveloperIcon />,
    },
  ];

  return removeOmittedMenuItems(items);
};
